/**
 * The sync flow (CLAUDE.md §10):
 *
 *   GET /v1/meta
 *     hash === the one on disk?  render from IndexedDB, transfer nothing
 *     else                        GET /v1/pull with If-None-Match
 *                                 decrypt, gunzip, zod-validate, store, render
 *
 * The passphrase-derived key never leaves memory. It is passed in per call
 * rather than held here, so there is no module-level place for it to linger.
 */
import { SchemaTooNewError, openSnapshotWithHash } from "@tally/shared";
import type { Snapshot } from "@tally/shared";

import { ApiError, fetchMeta, pullSnapshot, requestSync } from "./api.js";
import type { ApiConfig } from "./api.js";
import { loadSnapshot, saveSnapshot } from "./db.js";

export type SyncOutcome =
  | { status: "up-to-date"; snapshot: Snapshot; hash: string; syncedAt: string; generatedAt: string }
  | { status: "updated"; snapshot: Snapshot; hash: string; syncedAt: string; generatedAt: string }
  | { status: "offline"; snapshot: Snapshot | null; message: string }
  | { status: "error"; message: string; kind: "auth" | "passphrase" | "schema" | "other" };

export type SyncDeps = {
  now: () => Date;
};

const defaultDeps: SyncDeps = { now: () => new Date() };

export async function sync(
  config: ApiConfig,
  companyId: string,
  passphrase: string,
  deps: SyncDeps = defaultDeps,
): Promise<SyncOutcome> {
  const cached = await loadSnapshot(companyId);

  let remoteHash: string | null = null;
  try {
    const meta = await fetchMeta(config, companyId);
    remoteHash = meta.hash;

    if (cached && cached.hash === meta.hash) {
      return {
        status: "up-to-date",
        snapshot: cached.snapshot,
        hash: cached.hash,
        syncedAt: cached.syncedAt,
        generatedAt: cached.generatedAt,
      };
    }
  } catch (error) {
    if (error instanceof ApiError && error.status === 0) {
      // Offline. Every screen still works; only freshness suffers.
      return {
        status: "offline",
        snapshot: cached?.snapshot ?? null,
        message: cached
          ? "Offline — showing the last synced copy."
          : "Offline, and nothing has been synced to this device yet.",
      };
    }
    if (error instanceof ApiError && error.status === 401) {
      return { status: "error", message: error.message, kind: "auth" };
    }
    return {
      status: "error",
      message: error instanceof Error ? error.message : String(error),
      kind: "other",
    };
  }

  let blob: Uint8Array;
  try {
    const pulled = await pullSnapshot(config, companyId, cached?.hash ?? null);
    if (!pulled.changed) {
      if (!cached) {
        return {
          status: "error",
          message: "The server says nothing changed, but this device has no copy. Pull to refresh.",
          kind: "other",
        };
      }
      return {
        status: "up-to-date",
        snapshot: cached.snapshot,
        hash: cached.hash,
        syncedAt: cached.syncedAt,
        generatedAt: cached.generatedAt,
      };
    }
    blob = pulled.blob;
  } catch (error) {
    if (error instanceof ApiError && error.status === 0) {
      return {
        status: "offline",
        snapshot: cached?.snapshot ?? null,
        message: "Lost the connection mid-download. Showing the last synced copy.",
      };
    }
    return {
      status: "error",
      message: error instanceof Error ? error.message : String(error),
      kind: error instanceof ApiError && error.status === 401 ? "auth" : "other",
    };
  }

  let snapshot: Snapshot;
  let hash: string;
  try {
    const opened = await openSnapshotWithHash(blob, passphrase);
    snapshot = opened.snapshot;
    hash = opened.hash;
  } catch (error) {
    if (error instanceof SchemaTooNewError) {
      return {
        status: "error",
        kind: "schema",
        message: "This snapshot was made by a newer version of Ledgerly Go. Update the app.",
      };
    }
    return {
      status: "error",
      kind: "passphrase",
      message: "Wrong passphrase, or the download was corrupted.",
    };
  }

  // The hash is recomputed from the decrypted plaintext, so a mismatch means the
  // bytes that arrived are not the bytes the agent sealed.
  if (remoteHash && hash !== remoteHash) {
    return {
      status: "error",
      kind: "other",
      message: "The downloaded snapshot does not match its published hash. Not storing it.",
    };
  }

  const syncedAt = deps.now().toISOString();
  await saveSnapshot(companyId, snapshot, hash, syncedAt);

  return {
    status: "updated",
    snapshot,
    hash,
    syncedAt,
    generatedAt: snapshot.generatedAt,
  };
}

/* ------------------------------------------------------- on demand --- */

/** How long to wait for the laptop before giving up and saying so. */
export const REQUEST_SYNC_TIMEOUT_MS = 150_000;
const POLL_EVERY_MS = 4_000;

export type OnDemandProgress =
  | { phase: "asking" }
  | { phase: "waiting"; secondsWaited: number }
  | { phase: "downloading" };

/**
 * Ask the laptop to sync, then wait for the result to appear.
 *
 * The agent polls once a minute, so the usual wait is under 60 seconds. Polling
 * `/v1/meta` rather than pulling the blob means the wait costs a few hundred
 * bytes per check instead of 80 KB.
 *
 * A timeout is NOT an error — the laptop may simply be asleep. The caller is
 * told what happened and keeps the copy it already has.
 */
export async function syncNow(
  config: ApiConfig,
  companyId: string,
  passphrase: string,
  onProgress: (p: OnDemandProgress) => void,
  deps: SyncDeps = defaultDeps,
): Promise<SyncOutcome & { timedOut?: boolean }> {
  const cached = await loadSnapshot(companyId);
  const knownHash = cached?.hash ?? null;

  onProgress({ phase: "asking" });
  try {
    await requestSync(config, companyId);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return { status: "error", message: error.message, kind: "auth" };
    }
    if (error instanceof ApiError && error.status === 0) {
      return {
        status: "offline",
        snapshot: cached?.snapshot ?? null,
        message: "Offline — could not ask the laptop to sync.",
      };
    }
    return {
      status: "error",
      kind: "other",
      message: error instanceof Error ? error.message : String(error),
    };
  }

  const started = deps.now().getTime();
  for (;;) {
    const waited = deps.now().getTime() - started;
    if (waited >= REQUEST_SYNC_TIMEOUT_MS) break;

    onProgress({ phase: "waiting", secondsWaited: Math.round(waited / 1000) });
    await new Promise((resolve) => setTimeout(resolve, POLL_EVERY_MS));

    try {
      const meta = await fetchMeta(config, companyId);
      // A push always produces a fresh generatedAt, even when the books did not
      // change — so this notices the laptop responded either way.
      const changed =
        meta.hash !== knownHash ||
        (cached ? meta.generatedAt !== cached.generatedAt : true);
      if (!changed) continue;

      onProgress({ phase: "downloading" });
      return await sync(config, companyId, passphrase, deps);
    } catch {
      // A blip mid-wait is not fatal; keep waiting until the deadline.
    }
  }

  return {
    status: "offline",
    snapshot: cached?.snapshot ?? null,
    message:
      "The laptop did not respond within 2 minutes. It may be asleep, or Tally may be closed. " +
      "Showing the last synced copy.",
  };
}

/* --------------------------------------------------------- freshness --- */

/** CLAUDE.md §10: warn beyond six hours. */
export const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

export type Freshness = {
  label: string;
  ageMs: number;
  stale: boolean;
};

/**
 * Freshness is measured from when the AGENT generated the snapshot, not from
 * when this phone downloaded it. A phone that re-syncs every minute against a
 * laptop that has been asleep for a day is not fresh, and saying "synced 1
 * minute ago" would be the single most dangerous lie this app could tell.
 */
export function freshness(generatedAt: string, now: Date): Freshness {
  const generated = Date.parse(generatedAt);
  if (Number.isNaN(generated)) {
    return { label: "unknown", ageMs: Number.POSITIVE_INFINITY, stale: true };
  }

  const ageMs = Math.max(0, now.getTime() - generated);
  return { label: humanise(ageMs), ageMs, stale: ageMs > STALE_AFTER_MS };
}

function humanise(ageMs: number): string {
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}
